/**
 * DiabAssist Notification Service
 * Handles real-time push notifications via Gotify WebSocket
 */

import { isAuthenticated, getUserFromToken } from '@/lib/keycloak'
import { post, get } from '@/api/client'

// Types
interface NotificationConfig {
  clientToken: string
  websocketUrl: string
  gotifyUrl: string
}

interface GotifyMessage {
  id: number
  appid: number
  message: string
  title: string
  priority: number
  date: string
  extras?: {
    type?: string
    user_id?: string
    [key: string]: unknown
  }
}

type NotificationCallback = (notification: GotifyMessage) => void
type ConnectionCallback = (state: 'connected' | 'disconnected' | 'error') => void

// Singleton state
let config: NotificationConfig | null = null
let socket: WebSocket | null = null
let isConnected = false
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
let notificationCallbacks: NotificationCallback[] = []
let connectionCallbacks: ConnectionCallback[] = []

// Constants
const RECONNECT_DELAY = 5000
const NOTIFICATION_SERVICE_BASE = '/notifications'

/**
 * Initialize the notification service
 * Should be called once at app startup
 */
export async function initNotificationService(): Promise<void> {
  // Request browser notification permission
  if ('Notification' in window && Notification.permission === 'default') {
    await Notification.requestPermission()
  }

  console.log('[NotificationService] Initialized, permission:', Notification.permission)
}

/**
 * Provision notifications for the current user
 * This creates a Gotify client automatically and returns the WebSocket URL
 * Should be called after successful login
 */
export async function provisionNotifications(): Promise<boolean> {
  if (!isAuthenticated()) {
    console.warn('[NotificationService] Cannot provision - not authenticated')
    return false
  }

  const user = getUserFromToken()
  if (!user) {
    console.warn('[NotificationService] Cannot provision - no user')
    return false
  }

  try {
    console.log('[NotificationService] Provisioning for user:', user.id)

    const response = await post<NotificationConfig>(`${NOTIFICATION_SERVICE_BASE}/api/provision`, {
      user_id: user.id,
      device_name: `DiabAssist Web - ${navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop'}`,
      platform: 'web',
    })

    config = response
    console.log('[NotificationService] Provisioned successfully:', {
      hasToken: !!config.clientToken,
      wsUrl: config.websocketUrl?.substring(0, 50) + '...',
    })

    // Save to localStorage for reconnection
    localStorage.setItem('diabassist_notification_config', JSON.stringify(config))

    return true
  } catch (error) {
    console.error('[NotificationService] Provisioning failed:', error)
    return false
  }
}

/**
 * Load saved notification config from localStorage
 */
export function loadSavedConfig(): boolean {
  try {
    const saved = localStorage.getItem('diabassist_notification_config')
    if (saved) {
      config = JSON.parse(saved)
      console.log('[NotificationService] Loaded saved config')
      return true
    }
  } catch (error) {
    console.warn('[NotificationService] Failed to load saved config:', error)
  }
  return false
}

/**
 * Connect to the Gotify WebSocket to receive real-time notifications
 */
export function connect(): void {
  if (!config?.websocketUrl) {
    console.warn('[NotificationService] No WebSocket URL - call provisionNotifications first')
    return
  }

  if (isConnected && socket?.readyState === WebSocket.OPEN) {
    console.log('[NotificationService] Already connected')
    return
  }

  try {
    console.log('[NotificationService] Connecting to WebSocket...')
    socket = new WebSocket(config.websocketUrl)

    socket.onopen = () => {
      console.log('[NotificationService] WebSocket connected')
      isConnected = true
      notifyConnectionState('connected')

      // Clear any pending reconnect
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
        reconnectTimeout = null
      }
    }

    socket.onmessage = (event) => {
      try {
        const message: GotifyMessage = JSON.parse(event.data)
        console.log('[NotificationService] Received:', message.title)
        handleNotification(message)
      } catch (error) {
        console.error('[NotificationService] Failed to parse message:', error)
      }
    }

    socket.onerror = (error) => {
      console.error('[NotificationService] WebSocket error:', error)
      notifyConnectionState('error')
    }

    socket.onclose = () => {
      console.log('[NotificationService] WebSocket closed')
      isConnected = false
      notifyConnectionState('disconnected')
      scheduleReconnect()
    }
  } catch (error) {
    console.error('[NotificationService] Failed to connect:', error)
    scheduleReconnect()
  }
}

/**
 * Disconnect from WebSocket
 */
export function disconnect(): void {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout)
    reconnectTimeout = null
  }

  if (socket) {
    socket.close()
    socket = null
  }

  isConnected = false
  notifyConnectionState('disconnected')
}

/**
 * Clear all notification data (call on logout)
 */
export function clearNotifications(): void {
  disconnect()
  config = null
  localStorage.removeItem('diabassist_notification_config')
  console.log('[NotificationService] Cleared all notification data')
}

/**
 * Register a callback to receive notifications
 */
export function onNotification(callback: NotificationCallback): () => void {
  notificationCallbacks.push(callback)
  return () => {
    notificationCallbacks = notificationCallbacks.filter(cb => cb !== callback)
  }
}

/**
 * Register a callback for connection state changes
 */
export function onConnectionChange(callback: ConnectionCallback): () => void {
  connectionCallbacks.push(callback)
  return () => {
    connectionCallbacks = connectionCallbacks.filter(cb => cb !== callback)
  }
}

/**
 * Get connection status
 */
export function getConnectionStatus(): boolean {
  return isConnected
}

/**
 * Get notification history for current user
 */
export async function getNotificationHistory(): Promise<GotifyMessage[]> {
  const user = getUserFromToken()
  if (!user) return []

  try {
    return await get<GotifyMessage[]>(`${NOTIFICATION_SERVICE_BASE}/api/notifications/${user.id}`)
  } catch (error) {
    console.error('[NotificationService] Failed to get history:', error)
    return []
  }
}

// Private helpers

function handleNotification(message: GotifyMessage): void {
  // Show browser notification
  showBrowserNotification(message)

  // Notify all registered callbacks
  notificationCallbacks.forEach(callback => {
    try {
      callback(message)
    } catch (error) {
      console.error('[NotificationService] Callback error:', error)
    }
  })
}

function showBrowserNotification(message: GotifyMessage): void {
  if (!('Notification' in window)) return
  if (Notification.permission !== 'granted') return

  const options: NotificationOptions = {
    body: message.message,
    icon: '/favicon.ico',
    badge: '/favicon.ico',
    tag: `diabassist-${message.id}`,
    requireInteraction: message.priority >= 8, // Keep urgent notifications visible
    data: message.extras,
  }

  const notification = new Notification(message.title, options)

  notification.onclick = () => {
    window.focus()
    notification.close()

    // Handle navigation based on notification type
    const type = message.extras?.type
    if (type) {
      navigateToNotificationTarget(type)
    }
  }
}

function navigateToNotificationTarget(type: string): void {
  // Import router dynamically to avoid circular dependencies
  import('@/router').then(({ default: router }) => {
    switch (type) {
      case 'glucose_alert':
        router.push('/patient/glucose')
        break
      case 'medication_reminder':
        router.push('/patient/medication')
        break
      case 'appointment_reminder':
        router.push('/patient/appointments')
        break
      case 'community':
        router.push('/community')
        break
      case 'education':
        router.push('/education')
        break
      default:
        router.push('/')
    }
  })
}

function scheduleReconnect(): void {
  if (reconnectTimeout) return

  console.log(`[NotificationService] Reconnecting in ${RECONNECT_DELAY / 1000}s...`)
  reconnectTimeout = setTimeout(() => {
    reconnectTimeout = null
    if (!isConnected && config?.websocketUrl) {
      connect()
    }
  }, RECONNECT_DELAY)
}

function notifyConnectionState(state: 'connected' | 'disconnected' | 'error'): void {
  connectionCallbacks.forEach(callback => {
    try {
      callback(state)
    } catch (error) {
      console.error('[NotificationService] Connection callback error:', error)
    }
  })
}
